import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import React, { useCallback, useRef } from "react";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { PAST_CONVO_BASE_CLASS_NAME } from "organisms/modals/chat-modal/chat-past-conversations-panel";
import { StringUtils } from "@rsm-hcd/javascript-core";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import ChatOfflineTooltipContent from "organisms/modals/chat-modal/chat-offline-tooltip-content";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ChatHistorySearchProps {
    offline?: boolean;
    handleSearchText: React.Dispatch<React.SetStateAction<string>>;
    searchText: string;
    onShowSearch: React.Dispatch<React.SetStateAction<boolean>>;
    showSearch: boolean;
    onSearchTriggered: (searchText: string) => void;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS_NAME = "c-chat-history-search";
const DEBOUNCE_TIME = 750;

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatHistorySearch: React.FC<ChatHistorySearchProps> = ({
    offline = false,
    handleSearchText,
    onShowSearch,
    showSearch,
    searchText,
    onSearchTriggered,
}) => {
    const debouncedSearchTrigger = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const blurTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleInputBlur = () => {
        blurTimeout.current = setTimeout(() => {
            onShowSearch(false);
            handleSearchText("");
            onSearchTriggered("");
        }, 150);
    };

    const cancelBlur = () => {
        if (blurTimeout.current) {
            clearTimeout(blurTimeout.current);
            blurTimeout.current = null;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toLowerCase() === "enter") {
            immediatelyTriggerSearch();
        } else if (e.key.toLowerCase() === "escape") {
            e.stopPropagation();
            handleSearchText("");
            onShowSearch(false);
        }
    };

    const handleShowSearch = useCallback(() => {
        if (offline) {
            return;
        }

        // Open the search UI when the button is clicked
        onShowSearch(true);
    }, [offline, onShowSearch]);

    const clearDebounceTimeOut = useCallback(() => {
        if (debouncedSearchTrigger.current == null) return;
        clearTimeout(debouncedSearchTrigger.current);
        debouncedSearchTrigger.current = null;
    }, []);

    const immediatelyTriggerSearch = useCallback(() => {
        clearDebounceTimeOut();
        onSearchTriggered(searchText);
    }, [clearDebounceTimeOut, onSearchTriggered, searchText]);

    const handleSearchTextDebounce = useCallback(
        (value: string) => {
            const handleChange = (value: string) => {
                onSearchTriggered(value);
            };

            if (debouncedSearchTrigger != null) {
                clearTimeout(debouncedSearchTrigger.current!);
            }
            debouncedSearchTrigger.current = setTimeout(() => {
                handleChange(value);
            }, DEBOUNCE_TIME);
        },
        [onSearchTriggered]
    );

    const resetSearch = () => {
        cancelBlur();
        handleSearchText("");
        onSearchTriggered("");
        inputRef.current?.focus();
    };

    return (
        <div className={`${offline ? "-offline " : ""}`}>
            {!showSearch ? (
                <Tooltip
                    disabled={!offline}
                    content={<ChatOfflineTooltipContent />}
                    durationInMs={0}
                    hideOnClick={true}
                    placement={TooltipPlacement.AutoStart}>
                    <Button
                        size={ButtonSizes.Small}
                        cssClassName={`${PAST_CONVO_BASE_CLASS_NAME}__buttons`}
                        style={ButtonStyles.TertiaryAlt}
                        onClick={handleShowSearch}
                        aria-disabled={offline}>
                        <Icon
                            size={IconSizes.Base}
                            type={Icons.Search}
                            cssClassName="-search"
                        />
                        Search Chat
                    </Button>
                </Tooltip>
            ) : (
                <div
                    className={`${BASE_CLASS_NAME}__container`}
                    onMouseDown={cancelBlur}>
                    <input
                        ref={inputRef}
                        autoFocus={true}
                        className={`${BASE_CLASS_NAME}__input`}
                        onChange={(e) => {
                            handleSearchText(e.target.value);
                            handleSearchTextDebounce(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        onBlur={
                            !StringUtils.hasValue(searchText)
                                ? handleInputBlur
                                : undefined
                        }
                        value={searchText}
                    />

                    <Button
                        cssClassName={`${BASE_CLASS_NAME}__close-button`}
                        accessibleText={"Clear search"}
                        onClick={resetSearch}
                        size={ButtonSizes.Medium}
                        style={ButtonStyles.TertiaryAlt}>
                        <Icon
                            cssClassName={"-close-icon"}
                            size={IconSizes.Base}
                            type={Icons.Close}
                        />
                    </Button>
                </div>
            )}
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatHistorySearch;

// #endregion Exports
